<template>
  <div>
    <template v-if="!showCreateForm">
      <DataPage
        ref="historyTable"
        :columns="columns"
        :fetch-callback="fetchHistory"
        :model="EntityProductModel"
        :header-underline="false"
        :show-footer="false"
        has-row-actions
        headless
      >
        <template #actionButtons="{ item }">
          <div
            v-if="item.status !== 'disabled'"
            class="d-inline-block position-relative"
            @mouseenter="hoveredItemId = item.id"
            @mouseleave="hoveredItemId = null"
          >
            <b-button
              size="xs"
              variant="outline-secondary"
              class="dropdown-ellipses dropdown-toggle ml-2"
              @click.stop="clickedItemId = clickedItemId === item.id ? null : item.id"
            >
              <Icon icon="ellipsis-v" size="18"/>
            </b-button>
            <div
              v-if="hoveredItemId === item.id || clickedItemId === item.id"
              class="history-action-popover"
            >
              <b-button class="dropdown-item" size="sm" @click.prevent="disable(item)">
                <Icon icon="x-circle" class="mr-2" size="14"/>
                {{ __('buttons.disable') }}
              </b-button>
            </div>
          </div>
        </template>
      </DataPage>

      <div class="d-flex justify-content-start mt-3">
        <b-button variant="primary" size="sm" @click="showCreateForm = true">
          <Icon icon="plus-circle" size="14" class="mr-2"/>
          {{ __('buttons.add-new') }}
        </b-button>
      </div>
    </template>

    <template v-else>
      <b-row>
        <SelectField
          v-model="form.productId"
          class="col-12"
          name="product_id"
          :label="__('common.product')"
          :options="productOptions"
          :readonly="true"
        />
        <TextField
          v-model="form.commission"
          class="col-4"
          name="commission"
          :label="__('common.agent-commission') + ', %'"
          type="number"
        />
        <TextField
          v-model="form.grossAmount"
          class="col-4"
          name="gross_amount"
          :label="__('common.gross-commission')"
          type="number"
        />
        <TextField
          v-model="form.feeAmount"
          class="col-4"
          name="fee_amount"
          :label="__('entities.fee')"
          type="number"
        />
        <DateField
          v-model="form.activeFrom"
          class="col-4"
          name="active_from"
          :label="__('entities.active-from')"
        />
      </b-row>

      <div class="d-flex justify-content-between mt-3">
        <b-button size="sm" variant="secondary" @click="cancelCreate">
          {{ __('buttons.cancel') }}
        </b-button>
        <b-button size="sm" variant="primary" @click="submitCreate">
          <Icon icon="save" size="14" class="mr-2"/>
          {{ __('buttons.save') }}
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import ModalMixin from '@/General/Modal/ModalMixin.js'
import DataPage from '@/General/Tables/DataPage.vue'
import EntityProductModel from '@/Modules/Entity/EntityProduct/EntityProductModel.js'
import EntityProductService from '@/Modules/Entity/EntityProduct/EntityProductService.js'

export default {
  name: 'EntityProductHistoryModal',

  components: { DataPage },

  mixins: [ModalMixin],

  props: {
    meta: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },

  data() {
    return {
      EntityProductModel: EntityProductModel,
      showCreateForm: false,
      hoveredItemId: null,
      clickedItemId: null,

      form: {
        productId: { value: this.meta.productId, label: this.meta.productName },
        commission: null,
        grossAmount: null,
        feeAmount: null,
        activeFrom: new Date(),
        activeUntil: null,
        status: 'active',
      },

      productOptions: [
        { value: this.meta.productId, label: this.meta.productName },
      ],

      columns: [
        {
          column: 'commission',
          title: this.__('common.agent-commission') + ', %',
        },
        {
          column: 'grossAmount',
          title: this.__('common.gross-commission'),
        },
        {
          column: 'feeAmount',
          title: this.__('entities.fee'),
        },
        {
          column: 'activeFrom',
          title: this.__('entities.active-from'),
        },
        {
          column: 'activeUntil',
          title: this.__('entities.active-until'),
        },
        {
          column: 'status',
          title: this.__('common.status'),
        },
        {
          column: 'changeDate',
          title: this.__('entities.change-date'),
        },
        {
          column: 'changeUser',
          title: this.__('entities.change-user'),
        },
      ],
    }
  },

  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  },

  methods: {
    handleDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.clickedItemId = null
      }
    },

    fetchHistory(parameters) {
      return EntityProductService.history(
        this.meta.entityId,
        this.meta.productId,
        parameters
      )
    },

    disable(item) {
      EntityProductService
        .update(item.id, { status: 'disabled' })
        .then(() => {
          this.$refs.historyTable.refreshTable()
        })
        .catch(error => this.setErrorsFromResponse(error))
    },

    cancelCreate() {
      this.resetForm()
      this.showCreateForm = false
    },

    submitCreate() {
      EntityProductService
        .create(this.meta.entityId, {
          productId: this.meta.productId,
          commission: this.form.commission,
          grossAmount: this.form.grossAmount,
          feeAmount: this.form.feeAmount,
          activeFrom: this.form.activeFrom,
          activeUntil: this.form.activeUntil,
          status: this.$selectValue(this.form, 'status'),
        })
        .then(() => {
          this.$successToast(this.__('entities.product-link-success'))
          this.resetForm()
          this.showCreateForm = false
          this.$nextTick(() => {
            this.$refs.historyTable.refreshTable()
          })
        })
        .catch(error => this.setErrorsFromResponse(error))
    },

    resetForm() {
      this.form = {
        productId: { value: this.meta.productId, label: this.meta.productName },
        commission: null,
        grossAmount: null,
        feeAmount: null,
        activeFrom: new Date(),
        activeUntil: null,
        status: 'active',
      }
    },
  },
}
</script>

<style scoped>
::v-deep .dropdown-ellipses {
  padding: 0;
}
::v-deep .dropdown-ellipses i {
  margin-right: -2px;
  margin-left: -2px;
}

.history-action-popover {
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
  padding: 0.5rem;
  white-space: nowrap;
  z-index: 1060;
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.15);
}

.history-action-popover::before {
  content: '';
  position: absolute;
  right: -0.5rem;
  top: 50%;
  transform: translateY(-50%);
  border-top: 0.5rem solid transparent;
  border-bottom: 0.5rem solid transparent;
  border-left: 0.5rem solid rgba(0, 0, 0, 0.2);
}

.history-action-popover::after {
  content: '';
  position: absolute;
  right: calc(-0.5rem + 1px);
  top: 50%;
  transform: translateY(-50%);
  border-top: 0.5rem solid transparent;
  border-bottom: 0.5rem solid transparent;
  border-left: 0.5rem solid #fff;
}
</style>
